import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useTranslation } from 'react-i18next'

import * as datefns from 'date-fns'

import {
    Grid,
    Card as MuiCard,
    Divider as MuiDivider,
    Paper as MuiPaper,
    Typography as MuiTypography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    CardHeader,
    Button,
} from '@mui/material'
import { spacing } from '@mui/system'
import { getDateFnsLocale } from '../../../../i18n/localeUtils'
import DashboardApi from '../../../../api/DashboardApi'
import CacheApi from '../../../../api/CacheApi'
import { DeployInfo } from './DeployInfo'

const Paper = styled(MuiPaper)(spacing)
// import { green, red } from '@mui/material/colors'
const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Typography = styled(MuiTypography)(spacing)

/**
 * Show cache status, including which queries are cached and since when.
 * Also includes a button to clear the cache.
 */
// import { useState, useEffect } from 'react'
// import { Button, Card, CardHeader, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
// import datefns from 'date-fns'
// import sv from 'date-fns/locale/sv' // Assuming you have imported locale for date formatting
const dashboardApi = new DashboardApi()
const cacheApi = new CacheApi()

function CacheStatus() {
    const { i18n } = useTranslation()
    const dateFnsLocale = getDateFnsLocale(i18n.language)
    const [buttonDisabled, setButtonDisabled] = useState(false)
    const [timer, setTimer] = useState(0)
    const [cacheStatus, setCacheStatus] = useState(null)

    const fetcher = React.useCallback(async () => {
        const result = await dashboardApi.find('cache-status')
        setCacheStatus(result)
    }, [setCacheStatus])

    const handleButtonClick = () => {
        // Disable the button
        setButtonDisabled(true)

        // Invalidate cache
        cacheApi.invalidate()

        // Start a 60-second countdown
        setTimer(60)
    }

    useEffect(() => {
        fetcher()
    }, [fetcher])

    // Update the countdown timer every second if the timer is active
    useEffect(() => {
        let countdown
        if (timer > 0) {
            countdown = setInterval(() => {
                setTimer((prev) => prev - 1)
            }, 1000)
        }

        // When timer reaches 0, enable button and reset label
        if (timer === 0 && buttonDisabled) {
            fetcher()
            setButtonDisabled(false)
        }

        // Clear interval on cleanup
        return () => clearInterval(countdown)
    }, [timer, buttonDisabled, setCacheStatus])

    const content = []
    content.push(
        <Typography key={content.length} variant='caption' gutterBottom sx={{ mx: 4 }}>
            Cachade queries
        </Typography>,
    )

    // Add the button above the table
    content.push(
        <Button
            key={content.length}
            variant='contained'
            color='primary'
            onClick={handleButtonClick}
            disabled={buttonDisabled}
            sx={{ my: 2 }}
        >
            {buttonDisabled ? `Ladda om cache (${timer}s)` : 'Ladda om cache'}
        </Button>,
    )

    if (cacheStatus) {
        if (cacheStatus.length === 0) {
            content.push(
                <Typography key={content.length} variant='body1' sx={{ mx: 4 }}>
                    Inga cachade queries.
                </Typography>,
            )
        } else {
            content.push(
                <Table key={content.length} size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Objekt</TableCell>
                            <TableCell>Senast cachad</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cacheStatus.map((cache) => (
                            <TableRow key={cache.label}>
                                <TableCell>{cache.label}</TableCell>
                                <TableCell>
                                    {cache.creationDate &&
                                        datefns.format(new Date(cache.creationDate), 'yyyy-MM-dd HH:mm', {
                                            locale: dateFnsLocale,
                                        })}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>,
            )
        }
    }

    return (
        <Card mb={6}>
            <CardHeader title='Cache status'></CardHeader>
            <Paper>{content}</Paper>
        </Card>
    )
}

function Default() {
    return (
        <React.Fragment>
            <Helmet title='Admin Dashboard' />
            <Grid
                container
                spacing={6}
                sx={{
                    justifyContent: 'space-between',
                }}
            >
                <Grid>
                    <Typography variant='h3' gutterBottom>
                        Admin Dashboard
                    </Typography>
                    <DeployInfo />
                </Grid>
            </Grid>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                    <CacheStatus />
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default Default
